import React, { FC, useMemo } from 'react';

import pluralize from 'pluralize';

import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useBusinessUnitsTableData } from 'src/apps/dashboard/hooks/useBusinessUnitsTableData';
import { useConfigurationFilters } from 'src/apps/dashboard/hooks/useConfigurationFilters';
import { useDashboardDataQuery } from 'src/apps/dashboard/hooks/useDashboardDataQuery';
import { useScoreDataQuery } from 'src/apps/dashboard/hooks/useScoreDataQuery';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { isQueryLoading } from 'src/common/helpers/QueryHelpers';
import { useFiltersUrlSync } from 'src/common/hooks/useFiltersUrlSync';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { HEADER } from './Dashboard.constants';

export const AccessibilityDashboard: FC = () => {
	usePlatformConfigInitializer();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();
	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();

	const { filtersFromUrl, handleFilterChange } = useFiltersUrlSync({
		filterDefinitions,
		filterOptions
	});
	const effectiveFilters = useEffectiveFilters(filtersFromUrl, filterDefinitions);

	const dashboardDataQuery = useDashboardDataQuery(
		filtersFromUrl,
		labelsExcludedFromDashboard,
		filterDefinitions
	);
	const scoreDataQuery = useScoreDataQuery(
		filtersFromUrl,
		labelsExcludedFromDashboard,
		filterDefinitions
	);
	const [businessUnitsTableQuery] = useBusinessUnitsTableData({
		filters: filtersFromUrl,
		filterDefinitions
	});

	const isBusinessUnitsLoading = isQueryLoading(businessUnitsTableQuery);

	const filter = useMemo(
		() => ({
			filters: filtersFromUrl,
			filterDefinitions,
			filterOptions,
			onFilterChange: handleFilterChange
		}),
		[filtersFromUrl, filterDefinitions, filterOptions, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data
				? {
						...dashboardDataQuery.data,
						uniqueIssues:
							businessUnitsTableQuery.data?.summaryUniqueIssues ??
							dashboardDataQuery.data.uniqueIssues,
						totalIssues:
							businessUnitsTableQuery.data?.summaryTotalIssues ??
							dashboardDataQuery.data.totalIssues
					}
				: undefined,
			isLoading: dashboardDataQuery.isLoading || isBusinessUnitsLoading,
			error: dashboardDataQuery.error ?? businessUnitsTableQuery.error
		}),
		[
			dashboardDataQuery.data,
			dashboardDataQuery.isLoading,
			dashboardDataQuery.error,
			businessUnitsTableQuery.data,
			isBusinessUnitsLoading,
			businessUnitsTableQuery.error
		]
	);

	const bottom = useMemo(
		() => ({
			kind: 'businessUnitsTable' as const,
			title: businessUnitDisplayName ? pluralize(businessUnitDisplayName) : 'Business Units',
			query: {
				data: businessUnitsTableQuery.data,
				isLoading: isBusinessUnitsLoading,
				error: businessUnitsTableQuery.error
			}
		}),
		[
			businessUnitDisplayName,
			businessUnitsTableQuery.data,
			isBusinessUnitsLoading,
			businessUnitsTableQuery.error
		]
	);

	return (
		<DashboardTemplate
			header={HEADER}
			filter={filter}
			effectiveFilters={effectiveFilters}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
